import { getUserAvatarUrl, useAuth } from '@/contexts/AuthContext';
import { agentService } from '@common/api/agentService';
import { supabase } from '@common/api/authService';
import {
    Eye,
    EyeSlash,
    SignOut
} from '@phosphor-icons/react';
import React, {
    useEffect, useRef, useState
} from 'react';
import Modal from './Modal';

const ChangePasswordModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const [showNew, setShowNew] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const { error } = await supabase.auth.updateUser({password: newPassword});
            if (error) throw error;

            setSuccess(true);
            setTimeout(() => {
                onClose();
                setNewPassword('');
                setSuccess(false);
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Update password" width="max-w-md">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2">New Password</label>
                    <div className="relative">
                        <input 
                            type={showNew ? "text" : "password"}
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            disabled={loading || success}
                            className="w-full px-4 py-3 bg-blue-50/50 border-none rounded-xl text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-[#1A162F]/20 transition-all pr-10"
                        />
                        <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            {showNew ?  <Eye size={18} weight="bold" /> : <EyeSlash size={18} weight="bold" />}
                        </button>
                    </div>
                    {error ? <div className="text-red-500 text-sm mt-2">{error}</div> : null}
                    {success ? <div className="text-green-500 text-sm mt-2">Password updated successfully!</div> : null}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || success}
                        className="px-6 py-2.5 text-sm font-bold text-white bg-[#1A162F] hover:bg-[#1A162F]/90 rounded-xl shadow-sm transition-colors disabled:opacity-50"
                    >
                        {loading ? 'Updating...' : 'Confirm'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

const ProfileSettings = () => {
    const {
        user, logout, profile, setProfile
    } = useAuth();

    // Use metadata full_name if available, fallback to profile userName or email
    const [name, setName] = useState(user?.user_metadata?.full_name || user?.email?.split('@')[0] || '');
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isUpdatingName, setIsUpdatingName] = useState(false);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [avatarError, setAvatarError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // 当 user 元数据更新后，同步更新 name 状态
    useEffect(() => {
        if (user?.user_metadata?.full_name) {
            setName(user.user_metadata.full_name);
        }
    }, [user?.user_metadata?.full_name]);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('image/')) {
            setAvatarError('Please select an image file');
            return;
        }

        // 验证文件大小（最大 5MB）
        if (file.size > 5 * 1024 * 1024) {
            setAvatarError('Image size must be less than 5MB');
            return;
        }

        setIsUploadingAvatar(true);
        setAvatarError(null);

        try {
            // 1. 使用 agentService.uploadFile 上传文件
            const uploadResponse = await agentService.uploadFile(file);

            if (!uploadResponse.success) {
                throw new Error(uploadResponse.message || 'Upload failed');
            }

            // 2. 更新 Supabase 用户元数据
            const { data: updateData, error: updateError } = await supabase
                .auth
                .updateUser({data: {avatar_url: uploadResponse.file_url}});

            if (updateError) {
                throw updateError;
            }

        } catch (error: any) {
            console.error('Avatar upload failed:', error);
            setAvatarError(error.message || 'Failed to upload avatar');
        } finally {
            setIsUploadingAvatar(false);
            // 重置文件输入
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleNameSubmit = async () => {
        if (!name || name === user?.user_metadata?.full_name) return;

        setIsUpdatingName(true);
        try {
            // 更新 Supabase 用户元数据
            const { data: updateData, error: updateError } = await supabase.auth.updateUser({data: {full_name: name}});

            if (updateError) {
                throw updateError;
            }

            // 更新成功后，Supabase 会自动触发 onAuthStateChange 事件
            // AuthContext 中的 user 对象会自动更新，无需手动刷新
            console.log('姓名更新成功', updateData);

        } catch (error) {
            console.error('Failed to update name:', error);
        } finally {
            setIsUpdatingName(false);
        }
    };

    const handleLogout = () => {
        logout();
    };

    // 使用 user 的 updated_at 作为缓存破坏参数，确保头像更新后立即显示
    const cacheBuster = user?.updated_at ? String(new Date(user.updated_at).getTime()) : '';
    const avatarUrl = getUserAvatarUrl(user, { cacheBuster, fallbackName: name || 'User' });

    return (
        <div className="max-w-3xl">
            <div className="flex items-center gap-6 mb-8">
                {/* Avatar */}
                <div className="flex flex-col gap-2">
                    <div className="relative group cursor-pointer shrink-0" onClick={() => !isUploadingAvatar && fileInputRef.current?.click()}>
                        <img
                            src={avatarUrl}
                            className={`w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg ${isUploadingAvatar ? 'opacity-50' : ''}`}
                            alt="Profile"
                        />
                        <div className={`absolute inset-0 bg-black/40 rounded-full flex items-center justify-center transition-opacity ${isUploadingAvatar ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                            {isUploadingAvatar ? (
                                <div className="text-white text-xs font-bold">Uploading...</div>
                            ) : (
                                <span className="text-white text-xs font-bold">Change</span>
                            )}
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                                e.preventDefault();
                                handleAvatarUpload(e);
                            }}
                            disabled={isUploadingAvatar}
                        />
                    </div>
                    {avatarError ? <div className="text-red-500 text-xs text-center max-w-[100px]">{avatarError}</div> : null}
                </div>

                {/* Name and Logout */}
                <div className="flex-1 flex items-end justify-between">
                    <div className="flex-1 max-w-md">
                        <label className="block text-sm font-medium text-text-sub text-gray-500 mb-2">Name</label>
                        <input 
                            type="text" 
                            value={name} 
                            onChange={handleNameChange}
                            onBlur={handleNameSubmit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleNameSubmit();
                                    (e.target as HTMLInputElement).blur();
                                }
                            }}
                            disabled={isUpdatingName}
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-text-main text-gray-900 font-bold focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F] outline-none transition-all disabled:opacity-50"
                        />
                    </div>

                    {/* Logout Button */}
                    <button 
                        className="mb-[2px] ml-4 p-3 text-red-500 border border-gray-200 bg-white hover:border-red-200 hover:bg-red-50 rounded-xl transition-all shadow-sm"
                        onClick={handleLogout}
                        title="Log out"
                    >
                        <SignOut size={20} weight="bold" />
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-8 shadow-sm">
                {/* Email */}
                <div>
                    <label className="block text-sm font-medium text-text-sub text-gray-500 mb-2">Email</label>
                    <div className="text-text-main text-gray-900 font-medium">{user?.email}</div>
                </div>

                {/* Password */}
                <div className="border-t border-gray-100 pt-8">
                    <label className="block text-sm font-medium text-text-main text-gray-900 mb-4">Password</label>
                    <div className="flex items-center justify-between">
                        <div className="text-2xl text-text-sub text-gray-400 tracking-widest">••••••••••••</div>
                        <button 
                            onClick={() => setIsPasswordModalOpen(true)}
                            className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-bold text-text-main text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Update password
                        </button>
                    </div>
                </div>
            </div>
      
            {/* Password Modal */}
            <ChangePasswordModal 
                isOpen={isPasswordModalOpen} 
                onClose={() => setIsPasswordModalOpen(false)}
            />
        </div>
    );
};

export default ProfileSettings;
